import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, User, Phone, MessageSquare, CalendarDays, Trash2, CheckSquare, XSquare, ChevronDown, FileText } from 'lucide-react';
import { Lead, LeadState } from '../../types/lead';
import { StateBadge, IcpBadge } from '../ui/Badge';
import { useDataStore } from '../../context/DataStoreContext';
import { BookingModal } from '../booking/BookingModal';
interface LeadsTableProps {
  leads: Lead[];
}
export function LeadsTable({
  leads
}: LeadsTableProps) {
  const navigate = useNavigate();
  const {
    updateLead,
    deleteLead
  } = useDataStore();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bookingLeadId, setBookingLeadId] = useState<string | null>(null);
  const allSelected = leads.length > 0 && selectedIds.size === leads.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < leads.length;
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());else setSelectedIds(new Set(leads.map(l => l.id)));
  };
  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  const handleBulkVerify = (verified: boolean) => {
    selectedIds.forEach(id => updateLead(id, {
      VERIFICADO: verified
    }));
    setSelectedIds(new Set());
  };
  const handleBulkState = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newState = e.target.value as LeadState;
    if (!newState) return;
    selectedIds.forEach(id => updateLead(id, {
      STATE: newState
    }));
    setSelectedIds(new Set());
  };
  const handleBulkDelete = () => {
    if (window.confirm(`¿Estás seguro de eliminar ${selectedIds.size} leads? Esta acción no se puede deshacer.`)) {
      selectedIds.forEach(id => deleteLead(id));
      setSelectedIds(new Set());
    }
  };
  const maskPhone = (phone: string) => {
    if (!phone) return '';
    if (phone.length <= 6) return phone;
    const prefix = phone.substring(0, 3);
    const suffix = phone.substring(phone.length - 2);
    return `${prefix} *** ** ${suffix}`;
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short'
    });
  };
  const bookingLead = leads.find(l => l.id === bookingLeadId);
  return <div className="card overflow-hidden" data-id="element-490">
      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && <div className="bg-growthly-amber/10 border-b border-growthly-amber/20 px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-id="element-491">
          <div className="flex items-center gap-4 flex-wrap" data-id="element-492">
            <span className="text-[13px] font-semibold text-growthly-amber" data-id="element-493">
              {selectedIds.size} seleccionados
            </span>
            <div className="h-4 w-px bg-growthly-amber/20" data-id="element-494"></div>
            <button onClick={() => handleBulkVerify(true)} className="flex items-center text-[12px] font-medium text-growthly-text hover:text-white transition-colors" data-id="element-495">
              <CheckSquare className="w-4 h-4 mr-1.5 text-growthly-green" data-id="element-496" />
              Verificar
            </button>
            <button onClick={() => handleBulkVerify(false)} className="flex items-center text-[12px] font-medium text-growthly-text hover:text-white transition-colors" data-id="element-497">
              <XSquare className="w-4 h-4 mr-1.5 text-growthly-muted" data-id="element-498" />
              Quitar verificación
            </button>
            <div className="relative" data-id="element-499">
              <select onChange={handleBulkState} value="" className="appearance-none bg-transparent border-none text-[12px] font-medium text-growthly-text hover:text-white cursor-pointer focus:ring-0 pr-6" data-id="element-500">
                <option value="" disabled data-id="element-501">
                  Cambiar estado...
                </option>
                <option value="Nuevo" data-id="element-502">Nuevo</option>
                <option value="Contactado" data-id="element-503">Contactado</option>
                <option value="Conversación" data-id="element-504">Conversación</option>
                <option value="Micro-acuerdo" data-id="element-505">Micro-acuerdo</option>
                <option value="EnviadoPropuesta" data-id="element-506">Propuesta enviada</option>
                <option value="LeadCualificado" data-id="element-507">Lead Cualificado</option>
                <option value="Rechazado" data-id="element-508">Rechazado</option>
              </select>
              <ChevronDown className="absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-4 text-growthly-muted pointer-events-none" data-id="element-509" />
            </div>
          </div>
          <button onClick={handleBulkDelete} className="flex items-center text-[12px] font-medium text-growthly-red hover:text-red-400 transition-colors" data-id="element-510">
            <Trash2 className="w-4 h-4 mr-1.5" data-id="element-511" />
            Eliminar
          </button>
        </div>}

      <div className="overflow-x-auto" data-id="element-512">
        <table className="min-w-full divide-y divide-white/5" data-id="element-513">
          <thead className="bg-white/[0.02]" data-id="element-514">
            <tr data-id="element-515">
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-growthly-muted uppercase tracking-[0.12em] w-10" data-id="element-516">
                <input type="checkbox" checked={allSelected} ref={input => {
                if (input) input.indeterminate = someSelected;
              }} onChange={toggleSelectAll} className="rounded border-white/20 bg-growthly-bg text-growthly-amber focus:ring-growthly-amber" data-id="element-517" />
              </th>
              <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-growthly-muted uppercase tracking-[0.12em]" data-id="element-518">
                Negocio
              </th>
              <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-growthly-muted uppercase tracking-[0.12em]" data-id="element-519">
                Contacto
              </th>
              <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-growthly-muted uppercase tracking-[0.12em]" data-id="element-520">
                ICP / Score
              </th>
              <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-growthly-muted uppercase tracking-[0.12em]" data-id="element-521">
                Estado
              </th>
              <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-growthly-muted uppercase tracking-[0.12em]" data-id="element-522">
                Próx. Acción
              </th>
              <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-growthly-muted uppercase tracking-[0.12em]" data-id="element-523">
                Evidencia
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]" data-id="element-524">
            {leads.map(lead => {
            const isSelected = selectedIds.has(lead.id);
            const nextActionLabel = formatDate(lead.NEXT_ACTION_DATE);
            const hasEvidence = lead.EVIDENCE_COMPLETENESS > 0;
            return <tr key={lead.id} className={`h-[76px] transition-colors cursor-pointer group ${isSelected ? 'bg-growthly-amber/[0.04]' : 'hover:bg-white/[0.025]'}`} onClick={() => navigate(`/app/leads/${lead.id}`)} data-id="element-525">
                  <td className="px-5 whitespace-nowrap" onClick={e => e.stopPropagation()} data-id="element-526">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(lead.id)} className="rounded border-white/20 bg-growthly-bg text-growthly-amber focus:ring-growthly-amber" data-id="element-527" />
                  </td>
                  <td className="px-4" data-id="element-528">
                    <div className="flex flex-col justify-center" data-id="element-529">
                      <span className="text-[14px] font-semibold text-white group-hover:underline" data-id="element-530">
                        {lead.NOMBRE_NEGOCIO}
                      </span>
                      <span className="text-[12px] text-growthly-muted mt-0.5" data-id="element-531">
                        {lead.DIRECCION.ciudad || 'Sin ciudad'} ·{' '}
                        {lead.LEAD_SOURCE}
                      </span>
                    </div>
                  </td>
                  <td className="px-4" data-id="element-532">
                    <div className="flex items-center justify-between gap-2" data-id="element-533">
                      <div className="flex flex-col min-w-0" data-id="element-534">
                        <span className="text-[13px] text-growthly-text truncate" data-id="element-535">
                          {lead.DECISOR_NOMBRE || <span className="italic text-growthly-muted" data-id="element-536">
                              Sin nombre
                            </span>}
                        </span>
                        <div className="flex items-center text-[12px] text-growthly-muted mt-0.5" data-id="element-537">
                          <span data-id="element-538">
                            {maskPhone(lead.TELEFONO_WHATSAPP) || 'Sin teléfono'}
                          </span>
                          {lead.TELEFONO_WHATSAPP && (lead.VERIFICADO ? <CheckCircle2 className="w-3 h-3 text-growthly-green ml-1.5 flex-shrink-0" data-id="element-539" /> : <AlertCircle className="w-3 h-3 text-growthly-amber ml-1.5 flex-shrink-0" data-id="element-540" />)}
                        </div>
                      </div>
                      <div className="hidden group-hover:flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" onClick={e => e.stopPropagation()} data-id="element-541">
                        <a href={lead.TELEFONO_WHATSAPP ? `tel:${lead.TELEFONO_WHATSAPP}` : '#'} className="p-1.5 text-growthly-muted hover:text-white hover:bg-white/10 rounded-md transition-colors" title="Llamar" data-id="element-542">
                          <Phone className="w-3.5 h-3.5" data-id="element-543" />
                        </a>
                        <a href={lead.TELEFONO_WHATSAPP ? `https://wa.me/${lead.TELEFONO_WHATSAPP.replace(/\D/g, '')}` : '#'} target="_blank" rel="noopener noreferrer" className="p-1.5 text-growthly-muted hover:text-growthly-green hover:bg-white/10 rounded-md transition-colors" title="WhatsApp" data-id="element-544">
                          <MessageSquare className="w-3.5 h-3.5" data-id="element-545" />
                        </a>
                        <button onClick={() => setBookingLeadId(lead.id)} className="p-1.5 text-growthly-muted hover:text-white hover:bg-white/10 rounded-md transition-colors" title="Agendar" data-id="element-546">
                          <CalendarDays className="w-3.5 h-3.5" data-id="element-547" />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 whitespace-nowrap" data-id="element-548">
                    <div className="flex items-center gap-2.5" data-id="element-549">
                      <IcpBadge priority={lead.ICP_PRIORITY} data-id="element-550" />
                      <div className="flex flex-col w-14" data-id="element-551">
                        <span className="text-[13px] font-semibold text-white" data-id="element-552">
                          {lead.LEAD_SCORE}
                        </span>
                        <div className="w-full h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden" data-id="element-553">
                          <div className={`h-full rounded-full transition-all ${lead.LEAD_SCORE >= 70 ? 'bg-growthly-green' : lead.LEAD_SCORE >= 40 ? 'bg-growthly-amber' : 'bg-white/40'}`} style={{
                        width: `${Math.min(lead.LEAD_SCORE / 100 * 100, 100)}%`
                      }} data-id="element-554"></div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 whitespace-nowrap" data-id="element-555">
                    <div className="flex flex-col items-start gap-1" data-id="element-556">
                      <StateBadge state={lead.STATE} data-id="element-557" />
                      {lead.MICRO_ACCION_TYPE && <span className="text-[11px] text-growthly-muted" data-id="element-558">
                          {lead.MICRO_ACCION_TYPE}
                        </span>}
                    </div>
                  </td>
                  <td className="px-4 whitespace-nowrap" data-id="element-559">
                    {nextActionLabel ? <div className="flex items-center gap-2" data-id="element-560">
                        <div className="w-6 h-6 rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-growthly-muted" data-id="element-561">
                          <CalendarDays className="w-3 h-3" data-id="element-562" />
                        </div>
                        <span className="text-[13px] text-growthly-text" data-id="element-563">
                          {nextActionLabel}
                        </span>
                      </div> : <span className="text-[12px] text-growthly-muted/70 italic" data-id="element-564">
                        Sin programar
                      </span>}
                  </td>
                  <td className="px-4 whitespace-nowrap" data-id="element-565">
                    {hasEvidence ? <div className="flex items-center gap-2" data-id="element-566">
                        <span className="text-[13px] font-semibold text-white w-9" data-id="element-567">
                          {lead.EVIDENCE_COMPLETENESS}%
                        </span>
                        <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden" data-id="element-568">
                          <div className={`h-full rounded-full transition-all ${lead.EVIDENCE_COMPLETENESS === 100 ? 'bg-growthly-green' : lead.EVIDENCE_COMPLETENESS >= 50 ? 'bg-growthly-amber' : 'bg-white/40'}`} style={{
                      width: `${lead.EVIDENCE_COMPLETENESS}%`
                    }} data-id="element-569"></div>
                        </div>
                      </div> : <span className="inline-flex items-center gap-1.5 text-[12px] text-growthly-muted/70" data-id="element-570">
                        <FileText className="w-3.5 h-3.5" data-id="element-571" />
                        Sin evidencia
                      </span>}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3.5 border-t border-white/[0.04] flex items-center justify-between bg-white/[0.015]" data-id="element-572">
        <p className="text-[13px] text-growthly-muted" data-id="element-573">
          Mostrando <span className="font-medium text-white" data-id="element-574">1</span> a{' '}
          <span className="font-medium text-white" data-id="element-575">{leads.length}</span> de{' '}
          <span className="font-medium text-white" data-id="element-576">{leads.length}</span>{' '}
          resultados
        </p>
        <nav className="relative z-0 inline-flex rounded-md -space-x-px" aria-label="Pagination" data-id="element-577">
          <button className="relative inline-flex items-center px-3 py-1.5 rounded-l-md border border-white/10 bg-growthly-surface text-[12px] font-medium text-growthly-muted hover:bg-white/5 transition-colors" data-id="element-578">
            Anterior
          </button>
          <button className="relative inline-flex items-center px-3 py-1.5 border border-white/10 bg-white/10 text-[12px] font-semibold text-white" data-id="element-579">
            1
          </button>
          <button className="relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-white/10 bg-growthly-surface text-[12px] font-medium text-growthly-muted hover:bg-white/5 transition-colors" data-id="element-580">
            Siguiente
          </button>
        </nav>
      </div>

      <BookingModal isOpen={!!bookingLeadId} onClose={() => setBookingLeadId(null)} leadId={bookingLead?.id} leadName={bookingLead?.NOMBRE_NEGOCIO} data-id="element-581" />
    </div>;
}